import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/routes";
import { Clock, MapPin, ChevronRight } from "lucide-react";
import mateoLogo from "@/assets/mateo-logo.png";
import { formatDistanceToNowStrict } from "date-fns";

interface UpcomingSession {
  id: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  meeting_location_text: string | null;
  coach_started_at: string | null;
  learner_verified_start: boolean | null;
  experience_title: string;
  coach_name: string;
}

const SessionLiveBanner = () => {
  const [session, setSession] = useState<UpcomingSession | null>(null);
  const [countdown, setCountdown] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch the nearest upcoming session within 90 minutes
  useEffect(() => {
    const fetchSession = async () => {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        setSession(null);
        return;
      }
      setUserId(authSession.user.id);

      const now = new Date();
      const in90min = new Date(now.getTime() + 90 * 60 * 1000);
      // Also show sessions that started up to 30 min ago (in-progress)
      const flexWindow = new Date(now.getTime() - 30 * 60 * 1000);

      const { data } = await supabase
        .from("bookings")
        .select(`
          id, scheduled_at, duration_minutes, status, meeting_location_text,
          coach_started_at, learner_verified_start,
          experiences(title),
          coach_id
        `)
        .eq("learner_id", authSession.user.id)
        .in("status", ["confirmed", "pending"])
        .gte("scheduled_at", flexWindow.toISOString())
        .lte("scheduled_at", in90min.toISOString())
        .order("scheduled_at", { ascending: true })
        .limit(1);

      if (!data || data.length === 0) {
        // Also check for in-progress sessions (coach started)
        const { data: inProgress } = await supabase
          .from("bookings")
          .select(`
            id, scheduled_at, duration_minutes, status, meeting_location_text,
            coach_started_at, learner_verified_start,
            experiences(title),
            coach_id
          `)
          .eq("learner_id", authSession.user.id)
          .eq("status", "confirmed")
          .not("coach_started_at", "is", null)
          .order("scheduled_at", { ascending: true })
          .limit(1);

        if (!inProgress || inProgress.length === 0) {
          setSession(null);
          return;
        }
        
        const booking = inProgress[0];
        const { data: coachProfile } = await supabase
          .from("profiles_public")
          .select("full_name")
          .eq("id", booking.coach_id)
          .maybeSingle();

        setSession({
          id: booking.id,
          scheduled_at: booking.scheduled_at,
          duration_minutes: booking.duration_minutes,
          status: booking.status || "confirmed",
          meeting_location_text: booking.meeting_location_text,
          coach_started_at: booking.coach_started_at,
          learner_verified_start: booking.learner_verified_start,
          experience_title: (booking.experiences as any)?.title || "Session",
          coach_name: coachProfile?.full_name || "Coach",
        });
        return;
      }

      const booking = data[0];
      const { data: coachProfile } = await supabase
        .from("profiles_public")
        .select("full_name")
        .eq("id", booking.coach_id)
        .maybeSingle();

      setSession({
        id: booking.id,
        scheduled_at: booking.scheduled_at,
        duration_minutes: booking.duration_minutes,
        status: booking.status || "confirmed",
        meeting_location_text: booking.meeting_location_text,
        coach_started_at: booking.coach_started_at,
        learner_verified_start: booking.learner_verified_start,
        experience_title: (booking.experiences as any)?.title || "Session",
        coach_name: coachProfile?.full_name || "Coach",
      });
    };

    fetchSession();
    const interval = setInterval(fetchSession, 60000);

    // Re-fetch when app returns from background
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Update countdown every second
  useEffect(() => {
    if (!session) return;

    const tick = () => {
      const diff = new Date(session.scheduled_at).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown("NOW");
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setCountdown(mins > 0 ? `${mins} min` : `${secs}s`);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [session]);

  if (!session) return null;

  const diff = new Date(session.scheduled_at).getTime() - Date.now();
  const isNow = diff <= 0;
  const isImminent = diff <= 15 * 60 * 1000;
  const needsCheckIn = session.coach_started_at && !session.learner_verified_start;

  // Hide banner once both parties have checked in (session fully started)
  if (session.coach_started_at && session.learner_verified_start) return null;

  const getActionLabel = () => {
    if (needsCheckIn) return "Check In";
    if (isNow) return "Join";
    return countdown;
  };

  const handleClick = () => {
    navigate(`${routes.bookings()}?id=${session.id}`);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] flex justify-center pointer-events-none">
      <button
        onClick={handleClick}
        className={`
          pointer-events-auto mt-[env(safe-area-inset-top,0px)] mx-auto
          flex items-center gap-2 px-4 py-2 rounded-full
          shadow-lg backdrop-blur-xl
          transition-all duration-300 ease-out
          hover:scale-105 active:scale-95
          ${isNow || needsCheckIn
            ? "bg-destructive/90 text-destructive-foreground ring-2 ring-destructive ring-offset-2 ring-offset-background"
            : isImminent
            ? "bg-orange-500/90 text-white"
            : "bg-foreground/90 text-background"
          }
        `}
        style={{ marginTop: "max(env(safe-area-inset-top, 8px), 8px)" }}
      >
        {/* Mateo "M" pill icon */}
        <img src={mateoLogo} alt="Mateo" className="w-9 h-9 rounded-full object-contain" />

        {/* Session info */}
        <span className="text-sm font-semibold truncate max-w-[140px]">
          {session.experience_title}
        </span>

        {/* Divider */}
        <div className={`w-px h-4 ${
          isNow || needsCheckIn ? "bg-destructive-foreground/30" : isImminent ? "bg-white/30" : "bg-background/30"
        }`} />

        {/* Countdown / Action */}
        <span className="text-sm font-bold whitespace-nowrap">
          {getActionLabel()}
        </span>

        <ChevronRight className="h-3.5 w-3.5 opacity-60" />
      </button>
    </div>
  );
};

export default SessionLiveBanner;
